import React, { useState } from "react";
import {
  importAllData,
  validateModelData,
  clearAllModelData,
} from "../../../services/centralizedDataManager";
import { addRecentProject } from "../../../services/recentProjects";
import { setCurrentSessionProject } from "../../../services/currentProject";
import { saveDirectoryHandle } from "../../../services/fileHandleStorage";
import { useModelStore } from "../../../store/useModelStore";
import { LoadConfirmationDialog } from "./LoadConfirmationDialog";
import { importAllDataAuto } from "../../../services/centralizedDataManager";
import { useSettingsStore } from "@/store/useSettingsStore";

interface LoadProjectDialogProps {
  isOpen: boolean;
  onClose: () => void;
  hasUnsavedChanges?: () => boolean;
  onQuickSave?: () => Promise<void>;
}

export const LoadProjectDialog: React.FC<LoadProjectDialogProps> = ({
  isOpen,
  onClose,
  hasUnsavedChanges,
  onQuickSave,
}) => {
  const [projectFolder, setProjectFolder] = useState("");
  const [directoryHandle, setDirectoryHandle] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [foundFiles, setFoundFiles] = useState<{
    hasData: boolean;
    hasConfig: boolean;
  }>({ hasData: false, hasConfig: false });

  // Load confirmation dialog state
  const [isLoadConfirmDialogOpen, setIsLoadConfirmDialogOpen] = useState(false);
  const [pendingLoadData, setPendingLoadData] = useState<{
    folder: string;
    dirHandle: any;
  } | null>(null);

  const isElectron = typeof window !== "undefined" && window.electronAPI;
  const supportsFileSystemAccess =
    "showDirectoryPicker" in window && !isElectron;

  if (!isOpen) return null;

  const handleBrowse = async () => {
    setError("");
    setFoundFiles({ hasData: false, hasConfig: false });

    if (isElectron) {
      try {
        const result = await window.electronAPI.openFile({
          properties: ["openDirectory"],
          title: "Select Project Folder",
        });

        if (!result.canceled && result.filePaths && result.filePaths[0]) {
          const folderPath = result.filePaths[0];
          setProjectFolder(folderPath);
          await checkElectronFiles(folderPath);
        }
      } catch (err) {
        console.error("Browse error:", err);
        setError("Failed to select folder");
      }
    } else if (supportsFileSystemAccess) {
      try {
        const dirHandle = await (window as any).showDirectoryPicker({
          mode: "readwrite",
        });
        setDirectoryHandle(dirHandle);
        setProjectFolder(dirHandle.name);
        await checkBrowserFiles(dirHandle);
      } catch (err: any) {
        if (err.name !== "AbortError") {
          console.error("Browse error:", err);
          setError("Failed to select folder");
        }
      }
    } else {
      setError(
        "Your browser doesn't support folder selection. Please use Chrome/Edge 86+ or the desktop app."
      );
    }
  };

  const checkElectronFiles = async (folderPath: string) => {
    const platform = await window.electronAPI.getPlatform();
    const separator = platform === "win32" ? "\\" : "/";

    const dataPath = `${folderPath}${separator}data.fema`;
    const configPath = `${folderPath}${separator}config.fema`;

    try {
      const dataCheck = await window.electronAPI.openProject(dataPath);
      const configCheck = await window.electronAPI.openProject(configPath);

      setFoundFiles({
        hasData: dataCheck.success,
        hasConfig: configCheck.success,
      });

      if (!dataCheck.success || !configCheck.success) {
        const missing = [];
        if (!dataCheck.success) missing.push("data.fema");
        if (!configCheck.success) missing.push("config.fema");
        setError(`Missing files: ${missing.join(", ")}`);
      }
    } catch (err) {
      setError("Could not read files from the selected folder");
    }
  };

  const checkBrowserFiles = async (dirHandle: any) => {
    try {
      let hasData = false;
      let hasConfig = false;

      try {
        await dirHandle.getFileHandle("data.fema");
        hasData = true;
      } catch (e) {
        // File doesn't exist
      }

      try {
        await dirHandle.getFileHandle("config.fema");
        hasConfig = true;
      } catch (e) {
        // File doesn't exist
      }

      setFoundFiles({ hasData, hasConfig });

      if (!hasData || !hasConfig) {
        const missing = [];
        if (!hasData) missing.push("data.fema");
        if (!hasConfig) missing.push("config.fema");
        setError(`Missing files: ${missing.join(", ")}`);
      }
    } catch (err) {
      setError("Could not read files from the selected folder");
    }
  };

  const handleLoadClick = () => {
    if (!projectFolder) {
      setError("Please select a project folder");
      return;
    }

    if (!foundFiles.hasData || !foundFiles.hasConfig) {
      setError(
        "Required files (data.fema and config.fema) not found in folder"
      );
      return;
    }

    if (hasUnsavedChanges && hasUnsavedChanges()) {
      setPendingLoadData({
        folder: projectFolder,
        dirHandle: directoryHandle,
      });
      setIsLoadConfirmDialogOpen(true);
    } else {
      handleLoad();
    }
  };

  const handleSaveAndLoad = async () => {
    if (!pendingLoadData) return;

    setIsLoadConfirmDialogOpen(false);

    try {
      if (onQuickSave) {
        await onQuickSave();
      }

      setTimeout(() => {
        handleLoad();
        setPendingLoadData(null);
      }, 500);
    } catch (error) {
      console.error("Failed to save before load:", error);
      setError("Failed to save current project");
      setPendingLoadData(null);
    }
  };

  // Handle Discard & Load
  const handleDiscardAndLoad = () => {
    setIsLoadConfirmDialogOpen(false);
    handleLoad();
    setPendingLoadData(null);
  };

  // Handle Cancel
  const handleCancelLoad = () => {
    setIsLoadConfirmDialogOpen(false);
    setPendingLoadData(null);
  };

  const handleLoad = async () => {
    setError("");
    if (!projectFolder) {
      setError("Please select a project folder");
      return;
    }

    if (!foundFiles.hasData || !foundFiles.hasConfig) {
      setError(
        "Required files (data.fema and config.fema) not found in folder"
      );
      return;
    }

    setIsLoading(true);

    try {
      let dataContent: string;
      let configContent: string;
      let extractedProjectName: string;

      if (isElectron) {
        const platform = await window.electronAPI.getPlatform();
        const separator = platform === "win32" ? "\\" : "/";
        const dataPath = `${projectFolder}${separator}data.fema`;
        const configPath = `${projectFolder}${separator}config.fema`;

        const dataResult = await window.electronAPI.openProject(dataPath);
        const configResult = await window.electronAPI.openProject(configPath);

        if (!dataResult.success) {
          throw new Error(`Failed to read data.fema: ${dataResult.error}`);
        }

        if (!configResult.success) {
          throw new Error(`Failed to read config.fema: ${configResult.error}`);
        }

        dataContent =
          typeof dataResult.data === "string"
            ? dataResult.data
            : JSON.stringify(dataResult.data);

        configContent = JSON.stringify(configResult.data);

        extractedProjectName =
          projectFolder.split(/[\\/]/).pop() || "Untitled Project";

        setCurrentSessionProject(projectFolder);

        try {
          const projectName = projectFolder.split(/[\\/]/).pop() || "Unknown";
          await addRecentProject(projectName, projectFolder);
        } catch (err) {
          console.warn("Failed to update recent projects:", err);
        }
      } else {
        const dataFileHandle = await directoryHandle.getFileHandle("data.fema");
        const dataFile = await dataFileHandle.getFile();
        dataContent = await dataFile.text();

        const configFileHandle =
          await directoryHandle.getFileHandle("config.fema");
        const configFile = await configFileHandle.getFile();
        configContent = await configFile.text();

        const projectName = directoryHandle.name;
        extractedProjectName = directoryHandle.name;

        const parentFolder = "browser-projects";
        const fullPath = `${parentFolder}/${projectName}`;

        await saveDirectoryHandle(fullPath, directoryHandle);
        setCurrentSessionProject(fullPath);

        try {
          await addRecentProject(projectName, fullPath);
        } catch (err) {
          console.warn("Failed to update recent projects:", err);
        }
      }

      const configObj = JSON.parse(configContent);

      clearAllModelData();

      // Import terminal format data
      const importResult = importAllDataAuto(dataContent, false);

      if (!importResult.success) {
        throw new Error(`Import failed: ${importResult.errors.join(", ")}`);
      }

      // Apply config settings
      if (configObj) {
        useSettingsStore.setState({
          units: configObj.units,
          display: configObj.display,
          analysis: configObj.analysis,
          performance: configObj.performance,
        });
      }

      useModelStore.getState().setProjectName(extractedProjectName);

      setTimeout(() => {
        window.dispatchEvent(new CustomEvent("predefined_loads_updated"));
        window.dispatchEvent(
          new CustomEvent("predefined_truss_presets_updated")
        );
      }, 250);

      setSuccess(true);

      setTimeout(() => {
        setIsLoading(false);
        setSuccess(false);
        onClose();
        setProjectFolder("");
        setDirectoryHandle(null);
        setFoundFiles({ hasData: false, hasConfig: false });
      }, 1500);
    } catch (err: any) {
      setError(err.message || "Failed to load project");
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setProjectFolder("");
    setDirectoryHandle(null);
    setFoundFiles({ hasData: false, hasConfig: false });
    setError("");
    setSuccess(false);
    onClose();
  };

  // Get project name for confirmation dialog
  const getPendingProjectName = () => {
    if (!pendingLoadData) return "";
    if (isElectron) {
      return pendingLoadData.folder.split(/[\\/]/).pop() || "Selected Project";
    }
    return pendingLoadData.dirHandle?.name || "Selected Project";
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-gray-800 rounded-lg shadow-xl w-[520px] max-w-[90vw] border border-gray-700">
          <div className="flex items-center justify-between p-4 border-b border-gray-700">
            <h2 className="text-lg font-semibold text-white">Load Project</h2>
            <button
              onClick={handleCancel}
              className="text-gray-400 hover:text-white transition-colors"
            >
              ✕
            </button>
          </div>

          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Project Folder
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={projectFolder || "Not selected"}
                  readOnly
                  className={`flex-1 px-3 py-2 bg-gray-700 border rounded text-sm ${
                    projectFolder
                      ? "border-green-500 text-white"
                      : "border-gray-600 text-gray-400"
                  }`}
                  disabled
                />
                <button
                  onClick={handleBrowse}
                  disabled={isLoading}
                  className="px-3 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded transition-colors disabled:opacity-50 flex items-center gap-1"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
                    />
                  </svg>
                  Browse
                </button>
              </div>
            </div>

            {projectFolder && <div className="space-y-2"></div>}

            {/* {projectFolder && foundFiles.hasData && foundFiles.hasConfig && (
              <div className="p-3 bg-yellow-900/30 border border-yellow-500 rounded text-yellow-300 text-sm">
                ⚠️ Loading will clear all existing data in the current
                workspace!
              </div>
            )} */}

            {error && (
              <div className="p-3 bg-red-900/30 border border-red-500 rounded text-red-300 text-sm">
                ⚠️ {error}
              </div>
            )}

            {success && (
              <div className="p-3 bg-green-900/30 border border-green-500 rounded text-green-300 text-sm">
                ✓ Project loaded successfully!
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 p-4 border-t border-gray-700">
            <button
              onClick={handleCancel}
              disabled={isLoading}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleLoadClick}
              disabled={
                isLoading ||
                success ||
                !projectFolder ||
                !foundFiles.hasData ||
                !foundFiles.hasConfig
              }
              className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded transition-colors disabled:opacity-50 min-w-[70px]"
            >
              {isLoading ? "Loading..." : success ? "✓ Loaded" : "Load"}
            </button>
          </div>
        </div>
      </div>

      {/* Load Confirmation Dialog */}
      <LoadConfirmationDialog
        isOpen={isLoadConfirmDialogOpen}
        onCancel={handleCancelLoad}
        onDiscardAndLoad={handleDiscardAndLoad}
        onSaveAndLoad={handleSaveAndLoad}
        projectName={getPendingProjectName()}
      />
    </>
  );
};
